import type { NurseWithRelation } from "@vTypes/nurse-type";
import type { ShiftType } from "@utils/grade-util";

type BucketKey = string | number;

export type ShiftCoverageBucket<Key extends BucketKey> = {
  id: Key;
  label: string;
};

export type ShiftCoverageBucketResult = {
  hasError: boolean;
  assignedCount: number;
  requiredTotal: number;
  messages: string[];
  shiftShortages: Partial<
    Record<
      ShiftType,
      {
        required: number;
        eligible: number;
      }
    >
  >;
};

export type ShiftCoverageValidationResult<Key extends BucketKey> = {
  hasErrors: boolean;
  messages: string[];
  byBucket: Partial<Record<Key, ShiftCoverageBucketResult>>;
};

type ShiftCoverageSettings = Partial<Record<ShiftType, number | null>>;

const normalizeShift = (value: unknown): ShiftType | null => {
  const text = String(value ?? "")
    .trim()
    .toUpperCase();
  if (text === "D" || text === "E" || text === "N" || text === "M") {
    return text;
  }
  return null;
};

export const canNurseWorkShift = (
  nurse: NurseWithRelation,
  shift: ShiftType,
  activeShiftKeys: readonly ShiftType[]
): boolean => {
  if (!activeShiftKeys.includes(shift)) return false;

  const fixedShift = normalizeShift(nurse.fixed_shift);
  if (fixedShift) {
    return fixedShift === shift;
  }

  const allowedShifts = Array.isArray(nurse.is_night_nurse)
    ? nurse.is_night_nurse
        .map((item) => normalizeShift(item))
        .filter((item): item is ShiftType => item !== null)
    : [];

  if (allowedShifts.length === 0) {
    return true;
  }

  return allowedShifts.includes(shift);
};

export function validateShiftCoverage<Key extends BucketKey>({
  buckets,
  shiftSettings,
  nursesByBucket,
  shiftKeys,
}: {
  buckets: ShiftCoverageBucket<Key>[];
  shiftSettings: Partial<Record<Key, ShiftCoverageSettings>>;
  nursesByBucket: Partial<Record<Key, NurseWithRelation[]>>;
  shiftKeys: readonly ShiftType[];
}): ShiftCoverageValidationResult<Key> {
  const byBucket: Partial<Record<Key, ShiftCoverageBucketResult>> = {};
  const messages: string[] = [];

  buckets.forEach((bucket) => {
    const settings = shiftSettings[bucket.id];
    if (!settings) return;

    const nurses = nursesByBucket[bucket.id] ?? [];
    const assignedCount = nurses.length;
    const requiredTotal = shiftKeys.reduce(
      (sum, shift) => sum + (Number(settings[shift]) || 0),
      0
    );
    const bucketMessages: string[] = [];
    const shiftShortages: ShiftCoverageBucketResult["shiftShortages"] = {};

    if (requiredTotal > assignedCount) {
      bucketMessages.push(
        `${bucket.label}: 총 인원 부족 - 배정 ${assignedCount}명 / 최소 합계 ${requiredTotal}명`
      );
    }

    shiftKeys.forEach((shift) => {
      const required = Number(settings[shift]) || 0;
      if (required <= 0) return;

      const eligible = nurses.filter((nurse) =>
        canNurseWorkShift(nurse, shift, shiftKeys)
      ).length;

      if (eligible < required) {
        shiftShortages[shift] = { required, eligible };
        bucketMessages.push(
          `${bucket.label}: ${shift} 근무 가능 인원 부족 - 최소 ${required}명 / 가능 ${eligible}명`
        );
      }
    });

    if (bucketMessages.length === 0) return;

    byBucket[bucket.id] = {
      hasError: true,
      assignedCount,
      requiredTotal,
      messages: bucketMessages,
      shiftShortages,
    };
    messages.push(...bucketMessages);
  });

  return {
    hasErrors: messages.length > 0,
    messages,
    byBucket,
  };
}
